import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Settings as SettingsIcon, Globe, Shield, Bell, Mail, Layers, Play, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { UserManagementTab, LanguagesTab, RemindersTab, AutoInvoiceTab } from '../components/settings';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const { getAuthHeaders, user } = useAuth();
  const [languages, setLanguages] = useState([]);
  const [complianceSettings, setComplianceSettings] = useState({
    w9_required: true,
    nda_required: false
  });
  const [reminderSettings, setReminderSettings] = useState({
    reminder_24h: true,
    reminder_2h: true
  });
  const [autoInvoiceSettings, setAutoInvoiceSettings] = useState({
    enabled: false
  });
  const [autoInvoiceLogs, setAutoInvoiceLogs] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingReminders, setCheckingReminders] = useState(false);
  const [runningAutoInvoice, setRunningAutoInvoice] = useState(false);
  const [users, setUsers] = useState([]);
  const [passwordResetRequests, setPasswordResetRequests] = useState([]);
  const [dailyDispatchSettings, setDailyDispatchSettings] = useState({ enabled: false });
  const [sendingDispatch, setSendingDispatch] = useState(false);

  const fetchSettings = async () => {
    try {
      const [langRes, compRes, reminderRes, usersRes, logsRes, autoInvRes, autoInvLogsRes, pwResetRes, dispatchRes] = await Promise.all([
        fetch(`${API_URL}/api/settings/languages`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        }),
        fetch(`${API_URL}/api/settings/compliance`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        }),
        fetch(`${API_URL}/api/reminders/settings`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        }),
        user?.role === 'admin' || user?.role === 'super_admin' ? fetch(`${API_URL}/api/users`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        }) : Promise.resolve({ ok: false }),
        fetch(`${API_URL}/api/email-logs?limit=20`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        }),
        fetch(`${API_URL}/api/settings/auto-invoice`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        }),
        fetch(`${API_URL}/api/settings/auto-invoice/logs?limit=5`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        }),
        user?.role === 'super_admin' ? fetch(`${API_URL}/api/auth/password-reset-requests`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        }) : Promise.resolve({ ok: false }),
        fetch(`${API_URL}/api/settings/daily-dispatch`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        })
      ]);
      
      if (langRes.ok) {
        const data = await langRes.json();
        setLanguages(data);
      }
      if (compRes.ok) {
        const data = await compRes.json();
        setComplianceSettings(data);
      }
      if (reminderRes.ok) {
        const data = await reminderRes.json();
        setReminderSettings(data);
      }
      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data);
      }
      if (logsRes.ok) {
        const data = await logsRes.json();
        setEmailLogs(data);
      }
      if (autoInvRes.ok) {
        const data = await autoInvRes.json();
        setAutoInvoiceSettings(data);
      }
      if (autoInvLogsRes.ok) {
        const data = await autoInvLogsRes.json();
        setAutoInvoiceLogs(data);
      }
      if (pwResetRes.ok) {
        const data = await pwResetRes.json();
        setPasswordResetRequests(data.requests || []);
      }
      if (dispatchRes.ok) {
        const data = await dispatchRes.json();
        setDailyDispatchSettings(data);
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleUpdateCompliance = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/compliance`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(complianceSettings)
      });
      
      if (response.ok) {
        toast.success('Compliance settings updated');
      }
    } catch (error) {
      toast.error('Failed to update compliance settings');
    }
  };

  const handleToggleDailyDispatch = async (enabled) => {
    try {
      const response = await fetch(`${API_URL}/api/settings/daily-dispatch`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled })
      });
      
      if (response.ok) {
        setDailyDispatchSettings({ ...dailyDispatchSettings, enabled });
        toast.success(`Daily dispatch emails ${enabled ? 'enabled' : 'disabled'}`);
      }
    } catch (error) {
      toast.error('Failed to update daily dispatch settings');
    }
  };

  const handleSendDispatchNow = async () => {
    setSendingDispatch(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/daily-dispatch/send-now`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Dispatch emails sent');
      }
    } catch (error) {
      toast.error('Failed to send dispatch emails');
    } finally {
      setSendingDispatch(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Settings</h1>
      
      <Tabs defaultValue="languages">
        <TabsList className="mb-6 flex-wrap h-auto gap-1">
          <TabsTrigger value="languages" className="gap-2">
            <Globe className="h-4 w-4" />
            Languages
          </TabsTrigger>
          <TabsTrigger value="compliance" className="gap-2">
            <Shield className="h-4 w-4" />
            Compliance
          </TabsTrigger>
          <TabsTrigger value="reminders" className="gap-2">
            <Bell className="h-4 w-4" />
            Reminders
          </TabsTrigger>
          <TabsTrigger value="daily-dispatch" className="gap-2">
            <Mail className="h-4 w-4" />
            Daily Dispatch
          </TabsTrigger>
          <TabsTrigger value="auto-invoice" className="gap-2">
            <Layers className="h-4 w-4" />
            Auto-Invoice
          </TabsTrigger>
          {(user?.role === 'admin' || user?.role === 'super_admin') && (
            <TabsTrigger value="users" className="gap-2">
              <SettingsIcon className="h-4 w-4" />
              Users & Roles
            </TabsTrigger>
          )}
        </TabsList>

        {/* Languages Tab */}
        <TabsContent value="languages">
          <LanguagesTab 
            languages={languages}
            getAuthHeaders={getAuthHeaders}
            onRefresh={fetchSettings}
          />
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Vendor Compliance Requirements
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label>W-9 Required</Label>
                  <p className="text-sm text-muted-foreground">Require W-9 form before vendor activation</p>
                </div>
                <Switch
                  checked={complianceSettings.w9_required}
                  onCheckedChange={(checked) => setComplianceSettings({ ...complianceSettings, w9_required: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>NDA Required</Label>
                  <p className="text-sm text-muted-foreground">Require signed NDA before vendor activation</p>
                </div>
                <Switch
                  checked={complianceSettings.nda_required}
                  onCheckedChange={(checked) => setComplianceSettings({ ...complianceSettings, nda_required: checked })}
                />
              </div>
              
              <Button onClick={handleUpdateCompliance}>Save Changes</Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reminders Tab */}
        <TabsContent value="reminders">
          <RemindersTab
            reminderSettings={reminderSettings}
            setReminderSettings={setReminderSettings}
            checkingReminders={checkingReminders}
            setCheckingReminders={setCheckingReminders}
            getAuthHeaders={getAuthHeaders}
            onRefresh={fetchSettings}
          />
        </TabsContent>

        {/* Daily Dispatch Tab */}
        <TabsContent value="daily-dispatch">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Daily Dispatch Email
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Enable Daily Dispatch</Label>
                  <p className="text-sm text-muted-foreground">
                    Send daily email at 6 AM CT with today's and tomorrow's assignments to interpreters
                  </p>
                </div>
                <Switch
                  checked={dailyDispatchSettings?.enabled || false}
                  onCheckedChange={handleToggleDailyDispatch}
                />
              </div>
              
              <div className="border-t pt-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Manually send today's dispatch email to all interpreters with upcoming assignments.
                </p>
                <Button onClick={handleSendDispatchNow} disabled={sendingDispatch}>
                  {sendingDispatch ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Send Dispatch Now
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Email Logs */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Recent Email Logs</CardTitle>
            </CardHeader>
            <CardContent>
              {emailLogs && emailLogs.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {emailLogs.map((log, index) => (
                    <div key={index} className="p-3 rounded-lg border bg-muted/50">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{log.subject}</span>
                        <Badge variant={log.status === 'sent' ? 'default' : 'destructive'}>
                          {log.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        To: {log.to} • {new Date(log.sent_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No email logs yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Auto-Invoice Tab */}
        <TabsContent value="auto-invoice">
          <AutoInvoiceTab
            autoInvoiceSettings={autoInvoiceSettings}
            setAutoInvoiceSettings={setAutoInvoiceSettings}
            autoInvoiceLogs={autoInvoiceLogs}
            runningAutoInvoice={runningAutoInvoice}
            setRunningAutoInvoice={setRunningAutoInvoice}
            getAuthHeaders={getAuthHeaders}
            onRefresh={fetchSettings}
          />
        </TabsContent>

        {/* Users Tab */}
        {(user?.role === 'admin' || user?.role === 'super_admin') && (
          <TabsContent value="users">
            <UserManagementTab
              users={users}
              currentUser={user}
              passwordResetRequests={passwordResetRequests}
              getAuthHeaders={getAuthHeaders}
              onRefresh={fetchSettings}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default Settings;
